import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MenuIcon, ShareIcon, GridIcon, HeartIcon, BookmarkIcon, MoonIcon, SunIcon } from 'lucide-react';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { VideoGrid } from '../components/profile/VideoGrid';
import { MOCK_USERS } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
export function Profile() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Videos');
  const user = MOCK_USERS[0];
  const {
    theme,
    toggleTheme
  } = useTheme();
  const tabs = [{
    id: 'Videos',
    icon: GridIcon
  }, {
    id: 'Liked',
    icon: HeartIcon
  }, {
    id: 'Saved',
    icon: BookmarkIcon
  }];
  return <div className="w-full h-full bg-black flex flex-col" data-id="element-466">
      {/* Header */}
      <div className="px-4 py-4 flex justify-between items-center sticky top-0 bg-black/90 backdrop-blur-md z-10" data-id="element-467">
        <div className="w-8" data-id="element-468" />
        <h1 className="text-white font-poppins font-bold text-lg" data-id="element-469">
          {user.username}
        </h1>
        <button onClick={() => navigate('/settings')} className="text-white hover:text-softGray transition-colors" aria-label="Settings" data-id="element-470">
          <MenuIcon size={24} data-id="element-471" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar" data-id="element-472">
        {/* Profile Info */}
        <div className="flex flex-col items-center px-4 pt-4 pb-6" data-id="element-473">
          <Avatar src={user.avatar} size="xl" className="mb-4 border-2 border-surface2" data-id="element-474" />
          <h2 className="text-white font-bold text-xl mb-1" data-id="element-475">{user.name}</h2>
          <p className="text-softGray text-sm text-center whitespace-pre-line mb-6" data-id="element-476">
            {user.bio}
          </p>

          {/* Stats */}
          <div className="flex justify-center gap-8 w-full mb-6" data-id="element-477">
            <div className="flex flex-col items-center" data-id="element-478">
              <span className="text-white font-bold text-lg" data-id="element-479">
                {user.following}
              </span>
              <span className="text-softGray text-xs" data-id="element-480">Following</span>
            </div>
            <div className="w-px h-8 bg-border" data-id="element-481" />
            <div className="flex flex-col items-center" data-id="element-482">
              <span className="text-white font-bold text-lg" data-id="element-483">
                {user.followers}
              </span>
              <span className="text-softGray text-xs" data-id="element-484">Followers</span>
            </div>
            <div className="w-px h-8 bg-border" data-id="element-485" />
            <div className="flex flex-col items-center" data-id="element-486">
              <span className="text-white font-bold text-lg" data-id="element-487">{user.likes}</span>
              <span className="text-softGray text-xs" data-id="element-488">Likes</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 w-full max-w-[280px] mb-4" data-id="element-489">
            <Button variant="secondary" className="flex-1" onClick={() => navigate('/profile/edit')} data-id="element-490">
              Edit Profile
            </Button>
            <Button variant="secondary" className="px-4" aria-label="Share profile" data-id="element-491">
              <ShareIcon size={20} data-id="element-492" />
            </Button>
          </div>

          {/* Theme toggle */}
          <button onClick={toggleTheme} className="w-full max-w-[280px] flex items-center justify-between bg-surface border border-border rounded-full px-4 py-2.5 hover:bg-surface2 transition-colors" aria-label="Toggle theme" data-id="element-493">
            <span className="flex items-center gap-2 text-white text-sm font-medium" data-id="element-494">
              {theme === 'dark' ? <MoonIcon size={16} data-id="element-495" /> : <SunIcon size={16} data-id="element-496" />}
              {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </span>
            <motion.span layout className={`relative w-10 h-5 rounded-full transition-colors ${theme === 'dark' ? 'bg-primary' : 'bg-surface2'}`} data-id="element-497">
              <motion.span layout transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-md ${theme === 'dark' ? 'left-[22px]' : 'left-0.5'}`} data-id="element-498" />
            </motion.span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border sticky top-[60px] bg-black z-10" data-id="element-499">
          {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex-1 py-3 flex justify-center relative" data-id="element-500">
                <Icon size={24} className={`transition-colors ${isActive ? 'text-white' : 'text-softGray'}`} data-id="element-501" />
                {isActive && <motion.div layoutId="profileTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-502" />}
              </button>;
        })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px]" data-id="element-503">
          {activeTab === 'Videos' && <VideoGrid data-id="element-504" />}
          {activeTab === 'Liked' && <div className="flex flex-col items-center justify-center h-48 text-softGray" data-id="element-505">
              <HeartIcon size={48} className="mb-4 opacity-20" data-id="element-506" />
              <p data-id="element-507">Only you can see what you liked</p>
            </div>}
          {activeTab === 'Saved' && <div className="flex flex-col items-center justify-center h-48 text-softGray" data-id="element-508">
              <BookmarkIcon size={48} className="mb-4 opacity-20" data-id="element-509" />
              <p data-id="element-510">Your saved videos</p>
            </div>}
        </div>
      </div>
    </div>;
}